import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, XClose } from '@untitled-ui/icons-react'
import { MyConfirmModal, myToaster } from '@interstellar-component'

import { GolonganService } from '../../wallet.service'
import { golonganLabel, golonganReviewBadge } from '../../wallet.format'

/**
 * Golongan usaha pill under the company name, plus the UMKM application action.
 *
 * The pill always shows the golongan the company is billed at RIGHT NOW — never
 * the one it applied for. That distinction is the whole point of BR-SUB-07
 * poin 2/3: an application in flight changes nothing about today's prices, and
 * a pill that optimistically flipped to "UMKM" on submit would promise a
 * discount the company will not get on the invoice it is about to pay.
 */
function GolonganBadge({ status, onChanged }) {
  const navigate = useNavigate()
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!status) return null

  const golongan = status.golongan_usaha
  const review = status.golongan_review_status
  const reviewBadge = golonganReviewBadge(review)
  const isUmkm = golongan === 'umkm'
  // `unverified` (never applied) and `rejected` are both re-appliable — a
  // rejection is not terminal, the company can fix its documents and resubmit.
  const canApply = !isUmkm && (review === 'unverified' || review === 'rejected')

  const run = async (fn, okMessage) => {
    setBusy(true)
    try {
      await fn()
      myToaster({ status: 200, message: okMessage })
      if (onChanged) await onChanged()
    } catch (err) {
      myToaster(
        err?.response?.data || {
          status: 400,
          message: err?.message || 'Failed to process the request',
        },
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex items-center gap-1 rounded-full border border-gray/200 bg-gray/50 p-1 pl-1 pr-2">
        <span className="rounded-full border border-gray/200 bg-white px-2.5 py-0.5 text-sm-medium text-gray/700 shadow-xs">
          {golonganLabel(golongan)}
        </span>

        {canApply ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => navigate(`/company/${status.company_id}/umkm`)}
            className="flex items-center gap-1.5 rounded-full px-2 py-0.5 text-sm-medium text-brand/600 transition-colors hover:text-brand/700 disabled:opacity-50"
          >
            Apply as UMKM
            <ArrowRight className="size-4" />
          </button>
        ) : null}

        {reviewBadge && !isUmkm ? (
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm-medium ${reviewBadge.cls}`}
          >
            {reviewBadge.label}
          </span>
        ) : null}

        {/* Withdraw is offered only while nobody has picked the application up
            (can_cancel is BE-derived from pending_review). */}
        {status.can_cancel ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirmCancel(true)}
            className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-sm-medium text-gray/500 transition-colors hover:text-gray/700 disabled:opacity-50"
            title="Cancel application"
          >
            <XClose className="size-4" />
          </button>
        ) : null}
      </div>

      <MyConfirmModal
        negativeActionWord="Back"
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={() =>
          run(() => GolonganService.cancel(status.company_id), 'UMKM application canceled')
        }
        title="Cancel application"
        message="Your UMKM application will be withdrawn. You can apply again at any time."
        positiveActionWord="Cancel application"
        positiveButtonColor="error"
      />
    </>
  )
}

GolonganBadge.propTypes = {
  status: PropTypes.shape({
    company_id: PropTypes.string,
    golongan_usaha: PropTypes.string,
    golongan_review_status: PropTypes.string,
    can_cancel: PropTypes.bool,
  }),
  onChanged: PropTypes.func,
}

GolonganBadge.defaultProps = {
  status: null,
  onChanged: null,
}

export default GolonganBadge
